import { BackgroundCheckHistoryColumns, BackgroundCheckHistoryData } from '@/backend/features/background-check';
import {
    ParsedAddress,
    ParsedCases,
    ParsedCharge,
    ParsedPerson,
    ParsedSsnSubject,
    ParsedSsnValidation,
} from '@/backend/features/background-check/platform-functions/utils';
import { GetResponse } from '@juvare/platform-shared/features/storage';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { AppBar, Box, Button, Card, CardContent, DialogContent, Divider, Grid, IconButton, Toolbar, Typography } from '@mui/material';
import { TFunction } from 'i18next';
import React, { Fragment, ReactNode } from 'react';
import { downloadTextFile } from '../utils';

export function convertToTitleCase(text: string) {
    const result = text.replace(/([A-Z])/g, ' $1');

    return result.charAt(0).toUpperCase() + result.slice(1);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function renderKeyValue(object?: Record<string, any>, key?: string): ReactNode {
    if (typeof object === 'undefined' || typeof object === null || !key) {
        return null;
    }

    if (object[key] && typeof object[key] === 'boolean') {
        return (
            <Typography variant='body2' key={`${key}-${key}`}>
                <b>{convertToTitleCase(key)}:</b> {object[key] ? 'True' : 'False'}
            </Typography>
        );
    }

    if (object[key] && !Array.isArray(object[key]) && typeof object[key] === 'object') {
        return renderKeyValue(object, key);
    }

    if (typeof object[key] === 'string' || typeof object[key] === 'number') {
        return (
            <Typography variant='body2' key={`${key}-${key}`}>
                <b>{convertToTitleCase(key)}:</b> {object[key]}
            </Typography>
        );
    }

    return null;
}

function renderSsnSubjects(subjects?: ParsedSsnSubject[]) {
    const ssnSubjectKeys: Array<keyof ParsedSsnSubject> = [
        'type',
        'nameFirst',
        'nameLast',
        'streetNumber',
        'streetName',
        'streetSuffix',
        'city',
        'state',
        'zip',
        'county',
        'verified',
        'ssn',
        'phonePhone10',
        'phonePublicOrPrivate',
        'dob',
        'age',
        'dateFirst',
        'dateLast',
    ];

    if (!subjects) {
        return null;
    }

    return subjects.map((subject, index) => {
        const sKey = `subject-${index}`;

        return (
            <Card key={sKey} sx={{ maxWidth: 300, mt: 2 }}>
                <CardContent>
                    <Typography variant='h6' component='h3' gutterBottom>
                        SSN Record - {index + 1}
                    </Typography>
                    {ssnSubjectKeys.map((subjectKey) => {
                        if (subjectKey === 'dob' || subjectKey === 'dateFirst' || subjectKey === 'dateLast') {
                            return renderKeyValue(
                                { [subjectKey]: `${subject[subjectKey]?.month}/${subject[subjectKey]?.day}/${subject[subjectKey]?.year}` },
                                subjectKey,
                            );
                        }
                        return renderKeyValue(subject, subjectKey);
                    })}
                </CardContent>
            </Card>
        );
    });
}

function renderSsnValidation(ssnValidation?: ParsedSsnValidation) {
    const ssnValidationKeys: Array<keyof ParsedSsnValidation> = [
        'ssn',
        'stateIssuedBy',
        'firstIssuedYear',
        'lastIssuedYear',
        'ssnValid',
        'message',
        'death',
    ];

    if (!ssnValidation) return null;

    return (
        <Card sx={{ maxWidth: 300, mt: 2 }}>
            <CardContent>
                <Typography variant='h6' component='h3' gutterBottom>
                    SSN Verification
                </Typography>
                {ssnValidationKeys.map((ssnValidationKey) => {
                    return renderKeyValue(ssnValidation, ssnValidationKey);
                })}
            </CardContent>
        </Card>
    );
}

function renderPersons(persons?: ParsedPerson[]) {
    const personKeys: Array<keyof ParsedPerson> = [
        'lastName',
        'firstName',
        'dob',
        'sex',
        'race',
        'state',
        'county',
        'recordType',
        'matchCriteria',
    ];

    const addressKeys: Array<keyof ParsedAddress> = ['addressNo', 'streetNo', 'streetName', 'city', 'state', 'zip', 'importDate'];

    const caseKeys: Array<keyof ParsedCases> = ['caseNo', 'courtId', 'fileDate', 'caseNotes'];

    const chargeKeys: Array<keyof ParsedCharge> = [
        'caseNo',
        'chargeNo',
        'chargeCode',
        'chargeDesc',
        'dispoDate',
        'disposition',
        'offenseLevel',
    ];

    if (!persons) return null;

    return persons.map((person, index) => {
        const pKey = `person-${index}`;

        return (
            <Card key={pKey} sx={{ maxWidth: 300, mt: 2 }}>
                <CardContent>
                    <Typography variant='h6' component='h3' gutterBottom>
                        Match - {index + 1}
                    </Typography>
                    <Typography variant='body1' gutterBottom>
                        <b># Details</b>
                    </Typography>
                    {personKeys.map((personKey) => {
                        return renderKeyValue(person, personKey);
                    })}
                    {person.address && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant='body1' gutterBottom>
                                <b># Address</b>
                            </Typography>
                            {addressKeys.map((addressKey) => {
                                return renderKeyValue(person.address, addressKey);
                            })}
                        </>
                    )}
                    {person.cases &&
                        person.cases.map((caseEntity, index) => {
                            const cKey = `case-${index}-${pKey}`;

                            return (
                                <Fragment key={cKey}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant='body1' gutterBottom>
                                        <b># Case</b>
                                    </Typography>
                                    {caseKeys.map((caseKey) => {
                                        return renderKeyValue(caseEntity, caseKey);
                                    })}

                                    {caseEntity.charges &&
                                        caseEntity.charges.map((charge) => {
                                            const chKey = `case-${index}-${cKey}`;

                                            return (
                                                <Fragment key={chKey}>
                                                    <Divider sx={{ my: 2 }} />
                                                    <Typography variant='body1' gutterBottom>
                                                        <b># Charge</b>
                                                    </Typography>
                                                    {chargeKeys.map((chargeKey) => {
                                                        return renderKeyValue(charge, chargeKey);
                                                    })}
                                                </Fragment>
                                            );
                                        })}
                                </Fragment>
                            );
                        })}
                </CardContent>
            </Card>
        );
    });
}

interface RenderHistoryContentParams {
    close: (closed?: () => void) => void;
    backgroundCheckHistory: GetResponse<BackgroundCheckHistoryData, BackgroundCheckHistoryColumns>;
    t: TFunction<'rms'>;
    ariaTitleId: string;
    ariaContentId: string;
}

export const renderHistoryContent = ({ close, backgroundCheckHistory, t, ariaTitleId, ariaContentId }: RenderHistoryContentParams) => {
    const { data } = backgroundCheckHistory;

    return (
        <DialogContent id={ariaContentId}>
            <Box data-test='background-check-history'>
                <AppBar>
                    <Toolbar>
                        <IconButton edge='start' color='inherit' onClick={() => close()} aria-label='back' data-testid='back' size='large'>
                            <ArrowBack />
                        </IconButton>
                        <Typography variant='h6' sx={{ marginLeft: 2, flex: 1 }} id={ariaTitleId}>
                            {t('backgroundCheck.labels.backgroundCheckHistoryResult')}
                        </Typography>
                    </Toolbar>
                </AppBar>
                <Box sx={{ pt: 8, pr: 2, pl: 2, pb: 2 }}>
                    <Card sx={{ mt: 2 }}>
                        <CardContent>
                            <Typography variant='h6' component='h2' gutterBottom sx={{ display: 'flex' }}>
                                {t('backgroundCheck.history.details')}
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Typography variant='body2'>
                                        <b>{t('backgroundCheck.history.createdAt')}</b> {t('dateTime', { value: data.createdAt })}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Typography variant='body2' data-test='type'>
                                        <b>{t('backgroundCheck.history.type')}</b>{' '}
                                        {t(`backgroundCheck.types.${data.type}`, { defaultValue: 'Unknown' })}
                                    </Typography>
                                    {data.nextStatus && (
                                        <Typography variant='body2' data-test='status'>
                                            <b>{t('backgroundCheck.history.status')}</b>{' '}
                                            {t(`backgroundCheck.statuses.${data.nextStatus}`, {
                                                defaultValue: 'Unknown',
                                            })}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Typography variant='body2' data-test='message'>
                                        <b>{t('backgroundCheck.history.message')}</b> {data.note || data.message}
                                    </Typography>{' '}
                                    {data.requestedBy && (
                                        <Typography variant='body2'>
                                            <b>{t('backgroundCheck.history.requestedBy')}</b> {data.requestedBy}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    {data.responseXml && (
                                        <>
                                            <Divider sx={{ mb: 2 }} />
                                            <Button
                                                variant='outlined'
                                                onClick={() => downloadTextFile(data.responseXml ?? 'No content', 'result.xml')}
                                                data-test='xml-results-download-button'
                                            >
                                                {t('backgroundCheck.labels.downloadXmlResults')}
                                            </Button>
                                        </>
                                    )}
                                    {data.request && (
                                        <>
                                            <Divider sx={{ mb: 2 }} />
                                            <Button
                                                variant='outlined'
                                                onClick={() =>
                                                    downloadTextFile(
                                                        data.request ? JSON.stringify(data.request) : 'No content',
                                                        'request.json',
                                                    )
                                                }
                                                data-test='request-download-button'
                                            >
                                                {t('backgroundCheck.labels.downloadRequest')}
                                            </Button>
                                        </>
                                    )}
                                    {data.document && (
                                        <>
                                            <Divider sx={{ mb: 2 }} />
                                            <Button
                                                download={data.document.name}
                                                href={data.document.url}
                                                variant='outlined'
                                                data-test='document-download-button'
                                            >
                                                {t('backgroundCheck.labels.downloadDocument')}
                                            </Button>
                                        </>
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    {data.parsedXml && (
                        <Grid container spacing={2}>
                            {data.parsedXml.ssnValidationData && (
                                <Grid
                                    size={{
                                        xs: 12,
                                        md: 4,
                                    }}
                                >
                                    {renderSsnValidation(data.parsedXml.ssnValidationData)}
                                </Grid>
                            )}
                            {data.parsedXml.ssnSubjects && (
                                <Grid size={{ xs: 12, md: 4 }}>{renderSsnSubjects(data.parsedXml.ssnSubjects)}</Grid>
                            )}
                            {data.parsedXml.persons && <Grid size={{ xs: 12, md: 4 }}>{renderPersons(data.parsedXml.persons)}</Grid>}
                        </Grid>
                    )}
                </Box>
            </Box>
        </DialogContent>
    );
};
